// 7-day browser-local replay cache for completed takes
// (wiki/feature-local-recording-cache.md).
//
// Every practice calls `localRecordingCache.save(...)` on its end-of-session
// handler so the take survives F5 / tab close for 7 days. The cache is never
// the source of truth: an entry is dropped 7 days after `endedAt`, or as soon
// as the SERVER confirms it holds the bytes.
//
// Storage: IndexedDB, split across two object stores because IndexedDB has no
// projection — `get`/`getAll` return the whole record by structured clone, so
// a Blob sitting on the metadata record makes every listing deserialise every
// video. Metadata lives in `localRecordingMeta`; the recording and thumbnail
// bytes live in `localRecordingBlobs` as SEPARATE records (keyed
// `[sessionId, kind]`) so reading a card's thumbnail never pulls its video.
//
// The cache imposes NO size or entry-count ceiling of its own — the browser's
// quota is the only wall — and NEVER deletes an unexpired, unconfirmed take to
// make room for a new one (for a Free-tier user, with no upload path, that
// would be permanent loss). A refused write (`QuotaExceededError`, classified
// by `isStorageQuotaError`) rejects `save` and leaves every entry intact.
//
// Pure TS — no Svelte.

import type { MaterializeOverride } from '$lib/api/uploads';
import { promisify } from '../storage/idb';
import type { Practice, UploadReason } from '@voicedojo/backend-types';
import type { ExportCue } from '$lib/cues/cueExport';
import type { LiveConvoTake } from '$lib/liveConvo/liveConvo';

const DB_NAME = 'voicedojo-local-recordings';
// v2 added `pendingUploads`; v3 dropped the `endedAt` index; v4 split the one
// `localRecordings` store into metadata + bytes. Every upgrade preserves the
// takes already cached.
const DB_VERSION = 4;
const META_STORE = 'localRecordingMeta';
const BLOB_STORE = 'localRecordingBlobs';
const PENDING_STORE = 'pendingUploads';
const LEGACY_STORE = 'localRecordings';

/** The shelf-life of every cached take, in days — the single source for it. */
export const LOCAL_RECORDING_TTL_DAYS = 7;

/** The same shelf-life in milliseconds — what `expiresAt` is minted from. */
export const LOCAL_RECORDING_TTL_MS = LOCAL_RECORDING_TTL_DAYS * 24 * 60 * 60 * 1000;

/** A cached take assembled from both stores — metadata plus its bytes. */
export type LocalRecording = {
  /** Primary key — the on-device take id; the backend recording id when one exists. */
  sessionId: string;
  /** Recording bytes — `MediaRecorder` output (typically `video/webm`). */
  blob: Blob;
  /** Bare MIME type (`video/webm`, `audio/webm`, ...). */
  contentType: string;
  /** Display name — the auto-generated "Bemused Quokka" or user-renamed value. */
  sessionName: string;
  practiceKind: Practice;
  /** ISO string — when the take started (session-start, not record-start). */
  startedAt: string;
  /** ISO string — when the take ended. The cache mints `expiresAt` from this. */
  endedAt: string;
  /** ISO string — endedAt + 7d. Sweep drops entries past this. */
  expiresAt: string;
  durationSeconds: number;
  /** `blob.size` denormalised so listings can sum without reading any Blob. */
  sizeBytes: number;
  /**
   * The generated practice text (title line + body) the speaker read, mirroring
   * the post-take card's "Download practice text" `.txt`. Absent for practices
   * that generate none.
   */
  practiceText?: string;
  /** The coaching cues that fired during the take; absent on takes cached before they were kept. */
  cues?: ExportCue[];
  /** The user's private note; it reaches the server only with the take's upload. */
  notes?: string;
  /**
   * The Rehearsal Series the take was recorded into. Persisted so a take saved
   * and uploaded LATER from My library keeps its series link.
   */
  seriesId?: string;
  /**
   * The Series plan the take was rehearsed against, and the plan-point indices
   * that reached the speaker. Written as a PAIR or not at all — the indices are
   * positions in the schedule THAT plan produced.
   */
  seriesPlanId?: string;
  deliveredPlanPoints?: number[];
  /** The Series document revision the take was recorded against (Rehearsal). */
  seriesDocumentRevision?: number;
  /** A Live convo take's speaker-labelled conversation and its feedback facts, for a later Save / Review. */
  speakerPassage?: string;
  liveConvo?: LiveConvoTake;
  /**
   * A client-captured thumbnail for the take (a grabbed frame, or a user
   * replacement). Lives in the blob store under its own key, so a listing that
   * wants it reads it alone — never alongside the recording.
   */
  thumbnailBlob?: Blob;
};

/** Listing shape — everything the metadata store holds, i.e. no bytes at all. */
export type LocalRecordingMeta = Omit<LocalRecording, 'blob' | 'thumbnailBlob'>;

/**
 * The take facts an upload needs, as the live post-take path still holds them
 * in memory. Handed to the upload rather than read back out of the cache, so a
 * take uploads with its name, length and content even when IndexedDB is
 * unavailable (private mode, blocked storage) or its write has not landed yet.
 */
export type LocalTakeMeta = Pick<
  LocalRecordingMeta,
  'sessionName' | 'startedAt' | 'endedAt' | 'durationSeconds'
> & { practiceText?: string; cues?: ExportCue[] };

type BlobKind = 'recording' | 'thumbnail';
type BlobRecord = { sessionId: string; kind: BlobKind; blob: Blob };

/**
 * A durable journal entry for an in-flight upload
 * (wiki/feature-upload-reliability.md AC-FE1). Written after the take's
 * `Recording` is materialised and BEFORE the bytes are PUT; cleared once the
 * SERVER confirms. Carries no blob — the bytes live in the blob store under
 * `takeId`. Keyed by `recordingId`.
 */
export type PendingUpload = {
  /** Primary key — the server recording id the upload targets (bare GUID). */
  recordingId: string;
  /** The take's local id (== the cache key) the bytes live under. */
  takeId: string;
  /** Why the upload was kicked off — minted into the staging key on resume. */
  reason: UploadReason;
  /** Bare allowed MIME the signed URL and the PUT agree on. */
  contentType: string;
  /** `blob.size` — echoed to issue-url on resume. */
  sizeBytes: number;
  /** Epoch ms the entry was journalled. */
  createdAt: number;
  /** The save a home move's freeze refused, which the resume pass makes before sending any bytes. */
  saveFirst?: MaterializeOverride;
  /** The started multipart upload; a resume re-sends only the parts without an ETag. */
  multipart?: {
    objectKey: string;
    uploadId: string;
    partSizeBytes: number;
    etags: Record<number, string>;
  };
};

/** What `estimate()` returns. `ours` is computed from our entries. */
export type LocalRecordingEstimate = {
  usage: number;
  quota: number;
  /** Sum of `sizeBytes` across our entries. */
  ours: number;
};

/** Result of a sweep — how many expired entries were dropped. */
export type SweepResult = { expired: number };

/**
 * True when `err` is the browser's "storage is full" signal. This real failure
 * (not `navigator.storage.estimate()`, whose numbers are unreliable) is the
 * authority on "full".
 */
export function isStorageQuotaError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'QuotaExceededError';
}

// ─── IndexedDB plumbing ─────────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;
let persistRequested = false;

/**
 * Takes whose cache write is in flight, keyed by `sessionId`. {@link get} waits
 * on the entry here, so a reader started during a write never mistakes "not
 * written yet" for "not on this device".
 */
const inFlightWrites = new Map<string, Promise<unknown>>();

/**
 * Register a take's whole cache-write operation so a concurrent {@link get} of
 * the same take waits for it. Returns `work` unchanged — a rejection is the
 * caller's to handle, and never blocks a reader.
 */
function trackWrite<T>(sessionId: string, work: Promise<T>): Promise<T> {
  const settled = work.then(
    () => undefined,
    () => undefined,
  );
  inFlightWrites.set(sessionId, settled);
  void settled.then(() => {
    if (inFlightWrites.get(sessionId) === settled) inFlightWrites.delete(sessionId);
  });
  return work;
}

/** True when a usable IndexedDB exists (browser, not SSR / plain node test env). */
function isAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

/**
 * Move every v3 `localRecordings` record into the split shape, inside the
 * versionchange transaction, then drop the old store. A user mid-week with
 * unuploaded takes must not lose them, so the old store is deleted only after
 * the cursor has copied the last record.
 */
function migrateLegacyStore(db: IDBDatabase, tx: IDBTransaction): void {
  const legacy = tx.objectStore(LEGACY_STORE);
  const meta = tx.objectStore(META_STORE);
  const blobs = tx.objectStore(BLOB_STORE);
  const cursorRequest = legacy.openCursor();
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (!cursor) {
      db.deleteObjectStore(LEGACY_STORE);
      return;
    }
    const { blob, thumbnailBlob, ...rest } = cursor.value as LocalRecording;
    meta.put(rest);
    if (blob) blobs.put({ sessionId: rest.sessionId, kind: 'recording', blob });
    if (thumbnailBlob) {
      blobs.put({ sessionId: rest.sessionId, kind: 'thumbnail', blob: thumbnailBlob });
    }
    cursor.continue();
  };
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  if (!isAvailable()) {
    return Promise.reject(new Error('IndexedDB is not available in this environment'));
  }
  try {
    // The retired `voicedojo-offline` store held a copy of every take; this cache replaces it.
    indexedDB.deleteDatabase('voicedojo-offline');
  } catch {
    // best-effort
  }
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      const tx = request.transaction!;
      // Each create is guarded so a fresh DB and an upgrade from any older
      // version converge on the same shape without dropping live data.
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'sessionId' }).createIndex(
          'expiresAt',
          'expiresAt',
          { unique: false },
        );
      }
      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        db.createObjectStore(BLOB_STORE, { keyPath: ['sessionId', 'kind'] });
      }
      if (!db.objectStoreNames.contains(PENDING_STORE)) {
        db.createObjectStore(PENDING_STORE, { keyPath: 'recordingId' });
      }
      if (db.objectStoreNames.contains(LEGACY_STORE)) migrateLegacyStore(db, tx);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error('Failed to open the local-recording IndexedDB'));
  });
  return dbPromise;
}

async function readMeta(db: IDBDatabase, sessionId: string): Promise<LocalRecordingMeta | null> {
  const store = db.transaction(META_STORE, 'readonly').objectStore(META_STORE);
  const found = (await promisify(store.get(sessionId))) as LocalRecordingMeta | undefined;
  return found ?? null;
}

async function readBlob(db: IDBDatabase, sessionId: string, kind: BlobKind): Promise<Blob | null> {
  const store = db.transaction(BLOB_STORE, 'readonly').objectStore(BLOB_STORE);
  const found = (await promisify(store.get([sessionId, kind]))) as BlobRecord | undefined;
  return found?.blob ?? null;
}

/** Write the records of one call in a single transaction — all or nothing. */
async function writeAcross(
  db: IDBDatabase,
  meta: LocalRecordingMeta | null,
  blobs: BlobRecord[],
): Promise<void> {
  const tx = db.transaction([META_STORE, BLOB_STORE], 'readwrite');
  const writes: Promise<unknown>[] = [];
  if (meta) writes.push(promisify(tx.objectStore(META_STORE).put(meta)));
  for (const record of blobs) writes.push(promisify(tx.objectStore(BLOB_STORE).put(record)));
  const results = await Promise.allSettled(writes);
  const failed = results.find((r) => r.status === 'rejected');
  if (failed) throw (failed as PromiseRejectedResult).reason;
}

/** Drop a take entirely — its metadata and both of its blob records. */
async function deleteEntry(db: IDBDatabase, sessionId: string): Promise<void> {
  const tx = db.transaction([META_STORE, BLOB_STORE], 'readwrite');
  const blobs = tx.objectStore(BLOB_STORE);
  await Promise.all([
    promisify(tx.objectStore(META_STORE).delete(sessionId)),
    promisify(blobs.delete([sessionId, 'recording'])),
    promisify(blobs.delete([sessionId, 'thumbnail'])),
  ]);
}

async function listMeta(db: IDBDatabase): Promise<LocalRecordingMeta[]> {
  const store = db.transaction(META_STORE, 'readonly').objectStore(META_STORE);
  return (await promisify(store.getAll())) as LocalRecordingMeta[];
}

/**
 * Ask the browser to mark our storage persistent so it isn't evicted under
 * pressure (on Firefox it also lifts the ~10 GiB best-effort group limit).
 * Fires once; a denial doesn't block us.
 */
async function requestPersistOnce(): Promise<void> {
  if (persistRequested) return;
  persistRequested = true;
  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.storage &&
      typeof navigator.storage.persist === 'function'
    ) {
      await navigator.storage.persist();
    }
  } catch {
    // Ignore — the request is opportunistic.
  }
}

// ─── Public surface ─────────────────────────────────────────────────────────

/**
 * Drop entries past their 7-day `expiresAt`. Idempotent. Walks the `expiresAt`
 * index over `upperBound(now)` and deletes by primary key, so a sweep reads
 * only the keys it is about to drop — never a byte of video. The time-based
 * sweep and a server-confirmed upload are the only evictions; an unexpired,
 * unconfirmed take is never deleted to make room for another.
 */
async function sweep(): Promise<SweepResult> {
  if (!isAvailable()) return { expired: 0 };
  const db = await openDb();
  const index = db.transaction(META_STORE, 'readonly').objectStore(META_STORE).index('expiresAt');
  const range = IDBKeyRange.upperBound(new Date().toISOString());
  const expired = (await promisify(index.getAllKeys(range))) as string[];

  for (const sessionId of expired) {
    try {
      await deleteEntry(db, sessionId);
    } catch (err) {
      console.warn('localRecordingCache: failed to drop entry', sessionId, err);
    }
  }
  return { expired: expired.length };
}

/**
 * Persist a finished take. The cache mints `expiresAt = endedAt + 7d` and
 * `sizeBytes = blob.size`.
 *
 * Failure modes:
 * - IndexedDB unavailable (SSR / private mode) → resolves without writing.
 * - Sweep throw → swallowed; we still try to write.
 * - Put throw → logged + re-thrown so the caller can act on it (the blob is
 *   still in memory, so Download remains possible). Out of storage the
 *   rejection is the `QuotaExceededError` `DOMException`; every existing entry
 *   stays intact.
 */
async function save(input: Omit<LocalRecording, 'expiresAt' | 'sizeBytes'>): Promise<void> {
  if (!isAvailable()) return;
  try {
    await sweep();
  } catch (err) {
    console.warn('localRecordingCache: pre-save sweep failed', err);
  }
  void requestPersistOnce();

  const { blob, thumbnailBlob, ...rest } = input;
  const meta: LocalRecordingMeta = {
    ...rest,
    expiresAt: new Date(new Date(input.endedAt).getTime() + LOCAL_RECORDING_TTL_MS).toISOString(),
    sizeBytes: blob.size,
  };
  const records: BlobRecord[] = [{ sessionId: input.sessionId, kind: 'recording', blob }];
  if (thumbnailBlob) {
    records.push({ sessionId: input.sessionId, kind: 'thumbnail', blob: thumbnailBlob });
  }

  const db = await openDb();
  try {
    await writeAcross(db, meta, records);
  } catch (err) {
    if (isStorageQuotaError(err)) {
      console.warn(
        'localRecordingCache: browser storage is full — take not cached; existing takes untouched',
        err,
      );
    }
    throw err;
  }
}

/** Load every meta record, newest `endedAt` first. Reads no bytes. */
async function list(): Promise<LocalRecordingMeta[]> {
  if (!isAvailable()) return [];
  const all = await listMeta(await openDb());
  return all.sort((a, b) => b.endedAt.localeCompare(a.endedAt));
}

/**
 * One take's metadata, or `null` when absent. Reads no bytes — this is what a
 * surface that only needs the name, duration, text or mere presence calls.
 * Waits for the take's own write when one is still in flight.
 */
async function getMeta(sessionId: string): Promise<LocalRecordingMeta | null> {
  await inFlightWrites.get(sessionId);
  if (!isAvailable()) return null;
  return readMeta(await openDb(), sessionId);
}

/**
 * One take's thumbnail image, or `null`. Its own record in the blob store, so
 * a card fetching its thumbnail never deserialises the recording.
 */
async function getThumbnail(sessionId: string): Promise<Blob | null> {
  await inFlightWrites.get(sessionId);
  if (!isAvailable()) return null;
  return readBlob(await openDb(), sessionId, 'thumbnail');
}

/**
 * Load a whole take — metadata plus its bytes — or `null` when absent. Only
 * play / upload / download call this; anything listing takes uses
 * {@link getMeta}.
 */
async function get(sessionId: string): Promise<LocalRecording | null> {
  await inFlightWrites.get(sessionId);
  if (!isAvailable()) return null;
  const db = await openDb();
  const meta = await readMeta(db, sessionId);
  if (!meta) return null;
  const [blob, thumbnailBlob] = await Promise.all([
    readBlob(db, sessionId, 'recording'),
    readBlob(db, sessionId, 'thumbnail'),
  ]);
  if (!blob) return null;
  return { ...meta, blob, ...(thumbnailBlob ? { thumbnailBlob } : {}) };
}

/**
 * Replace a cached take's thumbnail in place. No-op when the take is absent or
 * IndexedDB is unavailable; touches nothing but the thumbnail record.
 */
async function setThumbnail(sessionId: string, thumbnailBlob: Blob): Promise<void> {
  if (!isAvailable()) return;
  const db = await openDb();
  if (!(await readMeta(db, sessionId))) return;
  await writeAcross(db, null, [{ sessionId, kind: 'thumbnail', blob: thumbnailBlob }]);
}

/** Patch a cached take's own metadata. No-op when the take is absent. */
async function patchMeta(
  sessionId: string,
  patch: Partial<Pick<LocalRecordingMeta, 'sessionName' | 'notes'>>,
): Promise<void> {
  if (!isAvailable()) return;
  const db = await openDb();
  const found = await readMeta(db, sessionId);
  if (!found) return;
  await writeAcross(db, { ...found, ...patch }, []);
}

/**
 * A device take has no server stream, so the cache entry IS its name and note
 * of record — the server commands never apply until it uploads.
 */
const rename = (sessionId: string, sessionName: string) => patchMeta(sessionId, { sessionName });
const setNotes = (sessionId: string, notes: string) => patchMeta(sessionId, { notes });

/** Delete a take by id — metadata and bytes. No-op when the take is absent. */
async function remove(sessionId: string): Promise<void> {
  if (!isAvailable()) return;
  await deleteEntry(await openDb(), sessionId);
}

// ─── Pending-upload journal (AC-FE1) ────────────────────────────────────────

/**
 * Journal an in-flight upload (idempotent on `recordingId`). Called after the
 * `Recording` is materialised and before the bytes are PUT, so a crash mid-PUT
 * leaves a durable record the resume pass can re-drive.
 */
async function putPending(entry: PendingUpload): Promise<void> {
  if (!isAvailable()) return;
  const db = await openDb();
  const store = db.transaction(PENDING_STORE, 'readwrite').objectStore(PENDING_STORE);
  await promisify(store.put(entry));
}

/** Load every journalled pending upload. Empty when IndexedDB is unavailable. */
async function listPending(): Promise<PendingUpload[]> {
  if (!isAvailable()) return [];
  const db = await openDb();
  const store = db.transaction(PENDING_STORE, 'readonly').objectStore(PENDING_STORE);
  return (await promisify(store.getAll())) as PendingUpload[];
}

/** Clear a journal entry by `recordingId`. No-op when absent or unavailable. */
async function removePending(recordingId: string): Promise<void> {
  if (!isAvailable()) return;
  const db = await openDb();
  const store = db.transaction(PENDING_STORE, 'readwrite').objectStore(PENDING_STORE);
  await promisify(store.delete(recordingId));
}

/**
 * The recorded length under which a cached take counts as ABANDONED — a
 * "started a practice then bailed" stub the page's Clean up action deletes.
 *
 * This is INDEPENDENT of the AI-Review minimum (`MinReviewDurationSeconds`,
 * also 30 s today): the two are equal by coincidence, not by rule. The review
 * floor is a capability bound; this is a client-side "was this a real attempt?"
 * heuristic. Moving the review floor must NOT move this. Do not merge them.
 */
export const ABANDONED_TAKE_MAX_SECONDS = 30;

/**
 * Delete every cached take whose recorded length is UNDER
 * {@link ABANDONED_TAKE_MAX_SECONDS} (wiki/feature-my-library.md
 * §"Clean up abandoned takes"). Client-side over the cache only. Returns the
 * number dropped so the page can report it.
 */
async function cleanupAbandoned(maxSeconds = ABANDONED_TAKE_MAX_SECONDS): Promise<number> {
  if (!isAvailable()) return 0;
  const db = await openDb();
  const victims = (await listMeta(db))
    .filter((e) => (e.durationSeconds ?? 0) < maxSeconds)
    .map((e) => e.sessionId);
  for (const sessionId of victims) {
    try {
      await deleteEntry(db, sessionId);
    } catch (err) {
      console.warn('localRecordingCache: failed to drop abandoned entry', sessionId, err);
    }
  }
  return victims.length;
}

/**
 * Storage estimate. `ours` sums `sizeBytes` across our metadata records; the
 * other two are whatever `navigator.storage.estimate()` reports.
 */
async function estimate(): Promise<LocalRecordingEstimate> {
  let usage = 0;
  let quota = 0;
  if (
    typeof navigator !== 'undefined' &&
    navigator.storage &&
    typeof navigator.storage.estimate === 'function'
  ) {
    try {
      const est = await navigator.storage.estimate();
      usage = typeof est.usage === 'number' ? est.usage : 0;
      quota = typeof est.quota === 'number' ? est.quota : 0;
    } catch {
      // Ignore — we surface zeros if the API throws.
    }
  }
  let ours = 0;
  if (isAvailable()) {
    try {
      ours = (await listMeta(await openDb())).reduce((acc, e) => acc + (e.sizeBytes ?? 0), 0);
    } catch {
      // Ignore — `ours = 0` is the best fallback.
    }
  }
  return { usage, quota, ours };
}

/** True when the runtime can host the cache (browser w/ IndexedDB). */
function isCacheAvailable(): boolean {
  return isAvailable();
}

/**
 * Reset internal state — tests only. NOT public API. Awaiting the returned
 * promise also closes the open handle, which a `deleteDatabase` or a version
 * upgrade would otherwise block on.
 */
function _reset(): Promise<void> {
  const closing = dbPromise;
  dbPromise = null;
  persistRequested = false;
  inFlightWrites.clear();
  return Promise.resolve(closing)
    .then((db) => db?.close())
    .catch(() => undefined);
}

export const localRecordingCache = {
  save,
  trackWrite,
  list,
  get,
  getMeta,
  getThumbnail,
  remove,
  setThumbnail,
  rename,
  setNotes,
  cleanupAbandoned,
  sweep,
  estimate,
  putPending,
  listPending,
  removePending,
  isAvailable: isCacheAvailable,
  /** Test-only seam. */
  _reset,
};
